import * as React from 'react'
import { useEffect, useState } from 'react'
import { IHydratedPageAnalysisConfig } from '@halo/interfaces/hydrated-page-analysis-config'
import { Button, Well } from '@pushly/aqe/lib/components'
import { Flex, Modal } from 'antd'
import { Container } from 'typescript-ioc/es5'
import { PageAnalysisConfigsService } from '@halo/services/page-analysis-configs.service'
import { simpleNotification } from '@halo/_utils/notifications'
import { CatalogItemType } from '@pushly/models/lib/enums/catalogs/catalog-item-type'
import { CatalogFieldMapping } from '@pushly/models/lib/structs/catalogs/catalog-field-mapping'
import { ItemIdStrategy } from '@pushly/models/lib/enums/catalogs/item-id-strategy'
import {
    CATALOG_DEFAULT_MAPPINGS,
    CATALOG_DEFAULT_SCHEMA_TYPES,
    REQUIRED_CATALOG_FIELDS,
} from '@halo/constants/catalog-default-mappings'
import EditCatalogFieldMappingDrawer from '@halo/components/edit-catalog-field-mapping-drawer/edit-catalog-field-mapping-drawer'
import { PlusOutlined } from '@ant-design/icons'
import CatalogConfigSection from './catalog-config/catalog-config-section'
import {
    DISPLAY_STRATEGY_JSONPATH_CUSTOM,
    IDrawerParams,
    ILocalCatalogConfig,
    ILocalItemIdConfig,
    createEmptyConfig,
    hydratedToLocal,
} from './catalog-config/types'
import './style/page-analysis-config_catalog-config.tab.scss'

export interface ICatalogConfigTabProps {
    hydratedPageAnalysisConfig: IHydratedPageAnalysisConfig
    onUpdate: (update: IHydratedPageAnalysisConfig) => void
    onLiveConfigsChange?: (configs: ILocalCatalogConfig[]) => void
}

function PageAnalysisCatalogConfigTab(props: ICatalogConfigTabProps) {
    const pageAnalysisConfigsService: PageAnalysisConfigsService = Container.get(PageAnalysisConfigsService)

    const [configs, setConfigs] = useState<ILocalCatalogConfig[]>([])
    const [isSaving, setIsSaving] = useState(false)
    const [isDirty, setIsDirty] = useState(false)
    const [expandedSections, setExpandedSections] = useState<Record<number, boolean>>({})
    const [drawerParams, setDrawerParams] = useState<IDrawerParams | null>(null)

    useEffect(() => {
        const existing = props.hydratedPageAnalysisConfig?.catalog_item_configs ?? []
        const loaded = existing.length > 0 ? hydratedToLocal(existing) : []
        setConfigs(loaded)
        setIsDirty(false)
        // Expand the first config by default — staring at a list of collapsed
        // headers gives no signal what's inside, and users typically want to
        // edit something rather than just inventory what's there.
        setExpandedSections(loaded.length > 0 ? { 0: true } : {})
    }, [props.hydratedPageAnalysisConfig])

    useEffect(() => {
        props.onLiveConfigsChange?.(configs)
    }, [configs])

    const canAddMore = configs.length < Object.values(CatalogItemType).length

    function toggleSection(idx: number) {
        setExpandedSections((prev) => ({ ...prev, [idx]: !prev[idx] }))
    }

    function getAvailableFields(configIndex: number): string[] {
        const config = configs[configIndex]
        if (!config?.item_type) return []
        const knownFields = CATALOG_DEFAULT_MAPPINGS[config.item_type]?.map((m) => m.catalog_field) ?? []
        const mappedFields = new Set(config.field_mappings.map((m) => m.catalog_field))
        return knownFields.filter((f) => !mappedFields.has(f))
    }

    function updateConfig(configIndex: number, patch: Partial<ILocalCatalogConfig>) {
        setConfigs((prev) => prev.map((c, i) => (i === configIndex ? { ...c, ...patch } : c)))
        setIsDirty(true)
    }

    function handleAddConfig() {
        const newIndex = configs.length
        setConfigs((prev) => [...prev, createEmptyConfig()])
        // Collapse all existing, expand only the new one.
        setExpandedSections({ [newIndex]: true })
        setIsDirty(true)
    }

    function handleRemoveConfig(configIndex: number) {
        Modal.confirm({
            title: 'Remove Configuration',
            content: 'Are you sure you want to remove this catalog item configuration?',
            onOk: () => {
                setConfigs((prev) => prev.filter((_, i) => i !== configIndex))
                setIsDirty(true)
            },
        })
    }

    function handleItemTypeChange(configIndex: number, newItemType: CatalogItemType) {
        const config = configs[configIndex]
        if (config.field_mappings.length > 0) {
            Modal.confirm({
                title: 'Change Item Type',
                content:
                    'Changing the item type will replace all existing field mappings with defaults. Continue?',
                onOk: () => applyItemTypeDefaults(configIndex, newItemType),
                onCancel: () => updateConfig(configIndex, { item_type: config.item_type }),
            })
        } else {
            applyItemTypeDefaults(configIndex, newItemType)
        }
    }

    function applyItemTypeDefaults(configIndex: number, newItemType: CatalogItemType) {
        updateConfig(configIndex, {
            item_type: newItemType,
            field_mappings: [...CATALOG_DEFAULT_MAPPINGS[newItemType]],
            schema_type: CATALOG_DEFAULT_SCHEMA_TYPES[newItemType],
        })
    }

    function handleAddMapping(configIndex: number) {
        setDrawerParams({ configIndex, mapping: {} })
    }

    function handleEditMapping(configIndex: number, mapping: CatalogFieldMapping, mappingIndex: number) {
        setDrawerParams({
            configIndex,
            mapping,
            mappingIndex,
            isRequired: REQUIRED_CATALOG_FIELDS.includes(mapping.catalog_field),
        })
    }

    function handleDrawerUpdate(mapping: CatalogFieldMapping, mappingIndex?: number) {
        if (!drawerParams) return
        const ci = drawerParams.configIndex
        setConfigs((prev) =>
            prev.map((c, i) => {
                if (i !== ci) return c
                if (mappingIndex != null) {
                    return {
                        ...c,
                        field_mappings: c.field_mappings.map((m, j) => (j === mappingIndex ? mapping : m)),
                    }
                }
                return { ...c, field_mappings: [...c.field_mappings, mapping] }
            }),
        )
        setDrawerParams(null)
        setIsDirty(true)
    }

    function handleDeleteMapping(configIndex: number, mappingIndex: number) {
        Modal.confirm({
            title: 'Delete Field Mapping',
            content: 'Are you sure you want to delete this field mapping?',
            onOk: () => {
                setConfigs((prev) =>
                    prev.map((c, i) =>
                        i === configIndex
                            ? { ...c, field_mappings: c.field_mappings.filter((_, j) => j !== mappingIndex) }
                            : c,
                    ),
                )
                setIsDirty(true)
            },
        })
    }

    function handleAddUrlConfig(configIndex: number) {
        setConfigs((prev) =>
            prev.map((config, idx) =>
                idx === configIndex
                    ? {
                          ...config,
                          item_id_configs: [...config.item_id_configs, { strategy: ItemIdStrategy.FULL_URL }],
                      }
                    : config,
            ),
        )
        setIsDirty(true)
    }

    function handleUpdateUrlConfigStrategy(configIndex: number, urlIndex: number, displayStrategy: string) {
        setConfigs((prev) =>
            prev.map((config, idx) => {
                if (idx !== configIndex) return config
                return {
                    ...config,
                    item_id_configs: config.item_id_configs.map((urlConfig, uidx) => {
                        if (uidx !== urlIndex) return urlConfig

                        let strategy: ItemIdStrategy
                        let defaults: Partial<ILocalItemIdConfig> = {}
                        const prevStrategy = urlConfig.strategy

                        switch (displayStrategy) {
                            case DISPLAY_STRATEGY_JSONPATH_CUSTOM:
                            case ItemIdStrategy.CONTENT_JSON_PATH:
                                strategy = ItemIdStrategy.CONTENT_JSON_PATH
                                defaults = {
                                    element_id: '__NEXT_DATA__',
                                    json_path:
                                        prevStrategy === ItemIdStrategy.CONTENT_JSON_PATH
                                            ? urlConfig.json_path ?? ''
                                            : '',
                                    regex_pattern:
                                        prevStrategy === ItemIdStrategy.CONTENT_JSON_PATH
                                            ? urlConfig.regex_pattern
                                            : undefined,
                                }
                                break
                            case ItemIdStrategy.LD_JSON_IDENTIFIER:
                                strategy = ItemIdStrategy.LD_JSON_IDENTIFIER
                                defaults = {}
                                break
                            default:
                                strategy = displayStrategy as ItemIdStrategy
                                defaults = { segment_index: undefined, after_segment: undefined }
                                break
                        }

                        return {
                            strategy,
                            segment_index: undefined,
                            after_segment: undefined,
                            json_path: undefined,
                            regex_pattern: undefined,
                            element_id: undefined,
                            _displayStrategy: displayStrategy,
                            ...defaults,
                        }
                    }),
                }
            }),
        )
        setIsDirty(true)
    }

    function handleUpdateUrlConfigField(
        configIndex: number,
        urlIndex: number,
        field: keyof ILocalItemIdConfig,
        value: string | number | undefined,
    ) {
        setConfigs((prev) =>
            prev.map((config, idx) =>
                idx === configIndex
                    ? {
                          ...config,
                          item_id_configs: config.item_id_configs.map((urlConfig, uidx) =>
                              uidx === urlIndex ? { ...urlConfig, [field]: value } : urlConfig,
                          ),
                      }
                    : config,
            ),
        )
        setIsDirty(true)
    }

    function handleDeleteUrlConfig(configIndex: number, urlIndex: number) {
        setConfigs((prev) =>
            prev.map((config, idx) =>
                idx === configIndex
                    ? {
                          ...config,
                          item_id_configs: config.item_id_configs.filter((_, uidx) => uidx !== urlIndex),
                      }
                    : config,
            ),
        )
        setIsDirty(true)
    }

    function handleMoveUrlConfig(configIndex: number, urlIndex: number, direction: 'up' | 'down') {
        setConfigs((prev) =>
            prev.map((config, idx) => {
                if (idx !== configIndex) return config
                const arr = [...config.item_id_configs]
                const targetIndex = direction === 'up' ? urlIndex - 1 : urlIndex + 1
                if (targetIndex < 0 || targetIndex >= arr.length) return config
                ;[arr[urlIndex], arr[targetIndex]] = [arr[targetIndex], arr[urlIndex]]
                return { ...config, item_id_configs: arr }
            }),
        )
        setIsDirty(true)
    }

    async function handleSave() {
        setIsSaving(true)
        try {
            const catalogItemConfigs = configs.map((config) => {
                const obj: Record<string, unknown> = {
                    enabled: config.enabled,
                    schema_type: config.schema_type || undefined,
                    item_type: config.item_type,
                    field_mappings: config.field_mappings.map((mapping) => ({
                        catalog_field: mapping.catalog_field,
                        json_path: mapping.json_path,
                        expected_type: mapping.expected_type ?? null,
                    })),
                    item_id_configs: config.item_id_configs.map((idConfig, index) => {
                        const { _displayStrategy, ...rest } = idConfig
                        return {
                            strategy: rest.strategy,
                            segment_index: rest.segment_index ?? null,
                            after_segment: rest.after_segment ?? null,
                            json_path: rest.json_path ?? null,
                            regex_pattern: rest.regex_pattern ?? null,
                            element_id: rest.element_id ?? null,
                            priority: index,
                        }
                    }),
                }
                if (config.id) obj.id = config.id
                return obj
            })

            await pageAnalysisConfigsService.patch(props.hydratedPageAnalysisConfig.id, {
                name: props.hydratedPageAnalysisConfig.name,
                catalogItemConfigs,
            } as Record<string, unknown>)

            const res = await pageAnalysisConfigsService.fetchById(props.hydratedPageAnalysisConfig.id, {
                query: { hydrated: true },
            })
            const refreshedConfig = res.data as IHydratedPageAnalysisConfig
            setConfigs(hydratedToLocal(refreshedConfig.catalog_item_configs ?? []))
            setIsDirty(false)
            simpleNotification('success', 'Catalog configs saved successfully.')
            props.onUpdate(refreshedConfig)
        } catch (e) {
            simpleNotification('error', e.toString())
        } finally {
            setIsSaving(false)
        }
    }

    function getAvailableItemTypesFor(configIndex: number): CatalogItemType[] {
        const config = configs[configIndex]
        const usedTypes = configs.map((c) => c.item_type).filter(Boolean) as CatalogItemType[]
        return Object.values(CatalogItemType).filter((t) => t === config.item_type || !usedTypes.includes(t))
    }

    if (!props.hydratedPageAnalysisConfig) return null

    return (
        <>
            {configs.length === 0 && (
                <Well className="type-2" hideHeader={true} footerMode="hidden">
                    <div className="catalog-config-empty">
                        <h3>No catalog extraction configured</h3>
                        <p>
                            Catalog extraction pulls structured item data (products, recipes, events…) from a
                            page's JSON-LD or DOM so the platform can target, segment, and recommend content
                            against it. Configure one extraction per item type to get started.
                        </p>
                    </div>
                </Well>
            )}

            {configs.map((config, configIndex) => (
                <CatalogConfigSection
                    key={configIndex}
                    config={config}
                    configIndex={configIndex}
                    isExpanded={!!expandedSections[configIndex]}
                    availableItemTypes={getAvailableItemTypesFor(configIndex)}
                    canAddMapping={getAvailableFields(configIndex).length > 0}
                    onToggleExpanded={() => toggleSection(configIndex)}
                    onUpdateConfig={(patch) => updateConfig(configIndex, patch)}
                    onRemoveConfig={() => handleRemoveConfig(configIndex)}
                    onChangeItemType={(t) => handleItemTypeChange(configIndex, t)}
                    onAddMapping={() => handleAddMapping(configIndex)}
                    onEditMapping={(mapping, mIdx) => handleEditMapping(configIndex, mapping, mIdx)}
                    onDeleteMapping={(mIdx) => handleDeleteMapping(configIndex, mIdx)}
                    onAddItemIdStrategy={() => handleAddUrlConfig(configIndex)}
                    onMoveItemIdStrategy={(uIdx, dir) => handleMoveUrlConfig(configIndex, uIdx, dir)}
                    onDeleteItemIdStrategy={(uIdx) => handleDeleteUrlConfig(configIndex, uIdx)}
                    onChangeItemIdStrategy={(uIdx, d) => handleUpdateUrlConfigStrategy(configIndex, uIdx, d)}
                    onChangeItemIdField={(uIdx, f, v) => handleUpdateUrlConfigField(configIndex, uIdx, f, v)}
                />
            ))}

            <div className="catalog-config-action-bar">
                <Flex justify="space-between" align="center" gap={8}>
                    <Button icon={<PlusOutlined />} disabled={!canAddMore} onClick={handleAddConfig}>
                        Add Configuration
                    </Button>
                    {configs.length > 0 && (
                        <Button
                            type="primary"
                            disabled={!isDirty || isSaving}
                            loading={isSaving}
                            onClick={handleSave}
                        >
                            {isSaving ? 'Saving…' : 'Save Catalog Config'}
                        </Button>
                    )}
                </Flex>
            </div>

            <EditCatalogFieldMappingDrawer
                params={
                    drawerParams
                        ? {
                              mapping: drawerParams.mapping,
                              index: drawerParams.mappingIndex,
                              isRequired: drawerParams.isRequired,
                          }
                        : null
                }
                availableFields={drawerParams ? getAvailableFields(drawerParams.configIndex) : []}
                defaultMappings={
                    drawerParams?.configIndex != null && configs[drawerParams.configIndex]?.item_type
                        ? CATALOG_DEFAULT_MAPPINGS[configs[drawerParams.configIndex].item_type]
                        : []
                }
                onClose={() => setDrawerParams(null)}
                onUpdate={handleDrawerUpdate}
            />
        </>
    )
}

export default PageAnalysisCatalogConfigTab
